import {
  Button,
  ControlledCommentBox,
  TextSemiBold,
  type ControlledCommentProps
} from '@hm/fundamental'
import {useCallback, useMemo, useState} from 'react'

import {useRiskSurveyTranslation} from '../../hooks'
import type {CommentDto} from '../../types'

type CommentsSectionProps = {
  comments: CommentDto[]
  canManage: boolean
  onAddComment: (text: string) => Promise<void>
  isSubmitting: boolean
  canLoadMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
}

const COMMENT_MAX_LENGTH = 2000

export const CommentsSection = ({
  comments,
  canManage,
  onAddComment,
  isSubmitting,
  canLoadMore,
  isLoadingMore,
  onLoadMore
}: CommentsSectionProps) => {
  const {t, i18n} = useRiskSurveyTranslation()
  const [newComment, setNewComment] = useState('')

  const mappedComments = useMemo<ControlledCommentProps<string>[]>(
    () =>
      comments.map((c) => ({
        id: c.id,
        name: c.userName,
        date: new Date(c.createdAt).toLocaleString(i18n.language),
        content: c.commentText,
        isDeletable: false,
        isEditable: false
      })),
    [comments, i18n.language]
  )

  const handleCommentChange = useCallback((value: string) => {
    setNewComment(value.slice(0, COMMENT_MAX_LENGTH))
  }, [])

  const handleSubmit = useCallback(
    async (text: string) => {
      if (!text.trim()) return

      try {
        await onAddComment(text.trim())
        setNewComment('')
      } catch {
        // Keep the draft intact when submission fails.
      }
    },
    [onAddComment]
  )

  const handleEditNoOp = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    async (_comment: ControlledCommentProps<string>, _content: string) => {},
    []
  )

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  const handleDeleteNoOp = useCallback(async (_id: string) => {}, [])

  return (
    <div>
      <TextSemiBold className="mb-2">{t('comments.title')}</TextSemiBold>
      <ControlledCommentBox<string>
        comments={mappedComments}
        commentInputValue={newComment}
        onCommentInputChange={handleCommentChange}
        onSubmit={handleSubmit}
        onEdit={handleEditNoOp}
        onDelete={handleDeleteNoOp}
        disabled={!canManage || isSubmitting}
      />
      {canLoadMore && (
        <Button
          variant="tertiary"
          onClick={onLoadMore}
          disabled={isLoadingMore}
          data-cy="comments-load-more-button"
        >
          {t('comments.loadMore')}
        </Button>
      )}
    </div>
  )
}
